import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  assignRole,
  deleteUser,
  listUsers,
  updateUser,
  type AdminUser,
  type ListUsersParams,
  type UpdateUserBody,
} from "../api/users";
import {
  createRole,
  deactivateRole,
  listRoles,
  updateRole,
  type AdminRole,
  type CreateRoleBody,
  type UpdateRoleBody,
} from "../api/roles";
import type { RoleCode } from "../types/auth-types/AuthUser";

/**
 * Server-state hook for the admin "All Users" table (§L — all HTTP I/O via
 * TanStack Query). `keepPreviousData` keeps the previous page on screen while
 * the next page loads, so paging/searching does not flash an empty table.
 */
export function useUsers(params: ListUsersParams) {
  return useQuery<AdminUser[]>({
    queryKey: ["users", "list", params],
    queryFn: () => listUsers(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

/**
 * Profile update for the Edit modal (PATCH /v1/users/:id). On success it
 * invalidates every "users" list page so the table reflects the change.
 */
export function useUpdateUser() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, Error, { id: string; body: UpdateUserBody }>({
    mutationFn: ({ id, body }) => updateUser(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}

/** All roles (the 5 §D.1 seeds + any custom roles) with permission booleans + admin metadata. */
export function useRoles() {
  return useQuery<AdminRole[]>({
    queryKey: ["roles"],
    queryFn: listRoles,
    staleTime: 5 * 60 * 1000,
  });
}

/** Create a custom role (POST /v1/admin/roles, ADR-0011); refreshes the roles cache. */
export function useCreateRole() {
  const queryClient = useQueryClient();
  return useMutation<AdminRole, Error, CreateRoleBody>({
    mutationFn: (body) => createRole(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
}

/** Edit an existing role (PATCH /v1/admin/roles/:id); refreshes the roles cache. */
export function useUpdateRole() {
  const queryClient = useQueryClient();
  return useMutation<AdminRole, Error, { id: number; body: UpdateRoleBody }>({
    mutationFn: ({ id, body }) => updateRole(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
}

/** Soft-deactivate a role (DELETE /v1/admin/roles/:id); refreshes the roles cache. */
export function useDeactivateRole() {
  const queryClient = useQueryClient();
  return useMutation<{ success: true }, Error, number>({
    mutationFn: (id) => deactivateRole(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["roles"] });
    },
  });
}

/**
 * Delete a user via DELETE /v1/users/:id (Cognito identity removed + DB soft
 * delete). On success it invalidates every "users" list page so the deleted
 * user drops out of the active-only table.
 */
export function useDeleteUser() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => deleteUser(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}

/** Change a user's role via POST /v1/users/:id/role. */
export function useAssignRole() {
  const queryClient = useQueryClient();
  return useMutation<AdminUser, Error, { id: string; roleCode: RoleCode }>({
    mutationFn: ({ id, roleCode }) => assignRole(id, roleCode),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["users", "list"] });
    },
  });
}
